/**
 * Theme Configuration
 * Central theme file for Lubumbashi Admin Panel
 */

// Theme modes
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
  AUTO: 'auto' // time-based switching
};

// Brand colors (preserved #505d69 as primary)
export const BRAND_COLORS = {
  primary: '#505d69',
  primaryLight: '#636f7b',
  primaryDark: '#3d4750',
  primaryRgb: '80, 93, 105',
  accent: '#5664d2',
  accentRgb: '86, 100, 210',
  success: '#1abc9c',
  successRgb: '26, 188, 156',
  warning: '#f1b44c',
  warningRgb: '241, 180, 76',
  danger: '#f46a6a',
  dangerRgb: '244, 106, 106',
  info: '#50a5f1',
  infoRgb: '80, 165, 241'
};

// Light theme values
export const LIGHT_THEME = {
  name: 'light',
  // Backgrounds
  bgPrimary: '#f8f9fa',
  bgSecondary: '#ffffff',
  bgCard: '#ffffff',
  bgSidebar: '#505d69',
  bgTopbar: '#ffffff',
  bgInput: '#ffffff',
  bgHover: 'rgba(86, 100, 210, 0.08)',

  // Text colors
  textPrimary: '#343a40',
  textSecondary: '#74788d',
  textMuted: '#adb5bd',
  textInverse: '#ffffff',

  // Borders
  borderColor: '#eff2f7',
  borderColorLight: '#f8f9fa',

  // Shadows
  shadowColor: 'rgba(0, 0, 0, 0.08)',
  shadowMd: '0 4px 24px rgba(0, 0, 0, 0.08)',
  shadowLg: '0 8px 32px rgba(0, 0, 0, 0.12)',

  // Sidebar specific
  sidebarBg: '#505d69',
  sidebarText: '#d7e4ec',
  sidebarTextHover: '#ffffff',
  sidebarIcon: '#8590a5',

  // Table
  tableStripeBg: '#f8f9fa',
  tableHoverBg: 'rgba(86, 100, 210, 0.05)'
};

// Dark theme values
export const DARK_THEME = {
  name: 'dark',
  // Backgrounds
  bgPrimary: '#1a1d21',
  bgSecondary: '#252b3b',
  bgCard: '#2a3042',
  bgSidebar: '#252b3b',
  bgTopbar: '#252b3b',
  bgInput: '#2a3042',
  bgHover: 'rgba(86, 100, 210, 0.15)',

  // Text colors
  textPrimary: '#e9ecef',
  textSecondary: '#8590a5',
  textMuted: '#636e75',
  textInverse: '#1a1d21',

  // Borders
  borderColor: '#32394e',
  borderColorLight: '#2a3042',

  // Shadows
  shadowColor: 'rgba(0, 0, 0, 0.3)',
  shadowMd: '0 4px 24px rgba(0, 0, 0, 0.3)',
  shadowLg: '0 8px 32px rgba(0, 0, 0, 0.4)',

  // Sidebar specific
  sidebarBg: '#252b3b',
  sidebarText: '#8590a5',
  sidebarTextHover: '#d7e4ec',
  sidebarIcon: '#636e75',

  // Table
  tableStripeBg: '#2a3042',
  tableHoverBg: 'rgba(86, 100, 210, 0.1)'
};

// Get theme based on time of day
export const getTimeBasedTheme = () => {
  const hour = new Date().getHours();
  // Light mode: 6 AM to 6 PM
  return (hour >= 6 && hour < 18) ? THEMES.LIGHT : THEMES.DARK;
};

// Get system preference
export const getSystemTheme = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? THEMES.DARK
      : THEMES.LIGHT;
  }
  return THEMES.LIGHT;
};

// Resolve theme mode to actual theme
export const resolveTheme = (mode) => {
  switch (mode) {
    case THEMES.LIGHT:
      return LIGHT_THEME;
    case THEMES.DARK:
      return DARK_THEME;
    case THEMES.SYSTEM:
      return getSystemTheme() === THEMES.DARK ? DARK_THEME : LIGHT_THEME;
    case THEMES.AUTO:
      return getTimeBasedTheme() === THEMES.DARK ? DARK_THEME : LIGHT_THEME;
    default:
      return LIGHT_THEME;
  }
};

// Get actual theme name (light/dark) from mode
export const getResolvedThemeName = (mode) => {
  switch (mode) {
    case THEMES.LIGHT:
      return THEMES.LIGHT;
    case THEMES.DARK:
      return THEMES.DARK;
    case THEMES.SYSTEM:
      return getSystemTheme();
    case THEMES.AUTO:
      return getTimeBasedTheme();
    default:
      return THEMES.LIGHT;
  }
};

// Theme storage key
export const THEME_STORAGE_KEY = 'lubumbashi-theme-mode';

// Default theme mode
export const DEFAULT_THEME_MODE = THEMES.AUTO;
